import React, { useMemo, useState, useEffect } from "react";
import { Link, createSearchParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const ExamCard = ({
  id,
  title,
  subtitle,
  color,
  icon,
  tag,
  updated,
  desc,
  resources,
  examObj,
  onEdit,
  onDelete,
  onColorChange,
}) => {
  const navigate = useNavigate();
  const [activeDropdown, setActiveDropdown] = useState(false);

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(false);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const colors = {
    red:    { gradient: "from-red-600 to-red-500", bgTag: "bg-red-50", textTag: "text-red-600", borderTag: "border-red-100", hoverBorder: "hover:border-red-200", textLight: "text-red-100", iconText: "text-red-500", circleBg: "bg-red-50", circleText: "text-red-600" },
    blue:   { gradient: "from-blue-600 to-blue-500", bgTag: "bg-blue-50", textTag: "text-blue-600", borderTag: "border-blue-100", hoverBorder: "hover:border-blue-200", textLight: "text-blue-100", iconText: "text-blue-500", circleBg: "bg-blue-50", circleText: "text-blue-600" },
    purple: { gradient: "from-purple-600 to-purple-500", bgTag: "bg-purple-50", textTag: "text-purple-600", borderTag: "border-purple-100", hoverBorder: "hover:border-purple-200", textLight: "text-purple-100", iconText: "text-purple-500", circleBg: "bg-purple-50", circleText: "text-purple-600" },
    orange: { gradient: "from-orange-500 to-amber-500", bgTag: "bg-orange-50", textTag: "text-orange-600", borderTag: "border-orange-100", hoverBorder: "hover:border-orange-200", textLight: "text-orange-100", iconText: "text-orange-500", circleBg: "bg-orange-50", circleText: "text-orange-600" },
    emerald:{ gradient: "from-emerald-600 to-teal-500", bgTag: "bg-emerald-50", textTag: "text-emerald-600", borderTag: "border-emerald-100", hoverBorder: "hover:border-emerald-200", textLight: "text-emerald-100", iconText: "text-emerald-500", circleBg: "bg-emerald-50", circleText: "text-emerald-600" },
    gray:   { gradient: "from-slate-600 to-slate-500", bgTag: "bg-slate-50", textTag: "text-slate-600", borderTag: "border-slate-100", hoverBorder: "hover:border-slate-200", textLight: "text-slate-100", iconText: "text-slate-500", circleBg: "bg-slate-50", circleText: "text-slate-600" },
  };

  const theme = colors[color] || colors.blue;
  const colorOptions = ["blue", "orange", "purple", "emerald", "red", "gray"];
  const colorDotClass = {
    blue: "bg-blue-500",
    orange: "bg-orange-500",
    purple: "bg-purple-500",
    emerald: "bg-emerald-500",
    red: "bg-red-500",
    gray: "bg-slate-500",
  };

  const to = useMemo(() => {
    const params = createSearchParams({
      title: title || "My Exams",
      color: color || "blue",
      examId: String(id || ""),
    }).toString();

    return `/board-prep/view?${params}`;
  }, [id, title, color]);

  const handleClick = (e) => {
    if (!id) {
      e.preventDefault();
      Swal.fire("Missing ID", "This exam card has no id. Check what you're passing to ExamCard.", "error");
      return;
    }
    navigate(to, { replace: false, state: { examId: String(id), title, color } });
  };

  return (
    <Link
      to={to}
      state={{ examId: String(id), title, color }}
      onClick={handleClick}
      className="block relative"
    >
      <div
        className={`group cursor-pointer bg-white rounded-2xl border border-gray-200 p-0 overflow-hidden hover:shadow-xl ${theme.hoverBorder} transition-all duration-300 relative`}
      >
        <div className={`h-32 bg-gradient-to-r ${theme.gradient} relative overflow-hidden`}>
          <div className="absolute inset-0 bg-white/10 opacity-10"></div>
          
          {/* ✅ DROPDOWN MENU ADDED HERE */}
          <div className="absolute top-4 right-4 z-20">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveDropdown(!activeDropdown);
              }}
              className="w-8 h-8 rounded-full flex items-center justify-center bg-black/20 text-white hover:bg-white hover:text-slate-800 transition-colors"
            >
              <i className="fa-solid fa-ellipsis-vertical"></i>
            </button>
            
            {activeDropdown && (
              <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-50 animate-in fade-in zoom-in-95 duration-100 origin-top-right">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onEdit();
                    setActiveDropdown(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
                >
                  <i className="fa-solid fa-pen mr-2"></i> Edit
                </button>
                {typeof onColorChange === "function" && (
                  <div className="px-4 py-2">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">Change color</p>
                    <div className="flex items-center gap-2">
                      {colorOptions.map((colorKey) => (
                        <button
                          key={colorKey}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onColorChange(colorKey);
                            setActiveDropdown(false);
                          }}
                          className={`w-5 h-5 rounded-full ${colors[colorKey].circleBg} ring-1 ring-offset-1 ${
                            color === colorKey ? "ring-slate-400 scale-110" : "ring-transparent"
                          } hover:scale-110 transition-transform`}
                          title={colorKey}
                        >
                          <span className={`block w-full h-full rounded-full ${colorDotClass[colorKey] || "bg-blue-500"}`}></span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDelete();
                    setActiveDropdown(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
                >
                  <i className="fa-solid fa-trash mr-2"></i> Delete
                </button>
              </div>
            )}
          </div>

          <div className="absolute bottom-4 left-6">
            <div className="bg-white/20 backdrop-blur-md p-2 rounded-lg inline-block mb-2">
              <i className={`fa-solid ${icon} text-white text-xl`}></i>
            </div>
            <h2 className="text-2xl font-bold text-white tracking-tight">{title}</h2>
            <p className={`${theme.textLight} text-xs font-medium`}>{subtitle}</p>
          </div>
        </div>

        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <span className={`${theme.bgTag} ${theme.textTag} text-xs font-bold px-2 py-1 rounded-md border ${theme.borderTag}`}>
              {tag}
            </span>
            <span className="text-gray-400 text-xs">
              <i className="fa-regular fa-clock mr-1"></i> {updated}
            </span>
          </div>

          <p className="text-gray-600 text-sm mb-6 leading-relaxed min-h-[40px] line-clamp-2">
            {desc}
          </p>

          <div className="flex items-center gap-4 text-xs font-medium text-gray-500 border-t border-gray-100 pt-4">
            <span className="flex items-center gap-1">
              <i className={`fa-solid fa-file-lines ${theme.iconText}`}></i> {resources}
            </span>
          </div>
        </div>

        <div className={`absolute bottom-6 right-6 w-10 h-10 ${theme.circleBg} rounded-full flex items-center justify-center ${theme.circleText} opacity-0 group-hover:opacity-100 transition-opacity transform translate-y-2 group-hover:translate-y-0`}>
          <i className="fa-solid fa-arrow-right"></i>
        </div>
      </div>
    </Link>
  );
};

export default ExamCard;
